import { useCallback, useEffect, useMemo, useRef } from 'react';
import { ObjectType } from '../../../Types/base';
import { WhiteboardFile, WhiteboardSignalREvent, WhiteboardSignalREventType } from '../../../Types/whiteboard';
import { fetchFileAndConvertToBase64DataUrl } from '../../../Utils/objectUtils';
import { HubKey } from '../../../Contexts/signalrContext';

export const useWhiteboardFiles = (
	whiteboardId: number | undefined,
	//eslint-disable-next-line @typescript-eslint/no-explicit-any
	excalidrawRef: React.MutableRefObject<any>,
	saveWhiteboardFile: (whiteboardId: number, file: WhiteboardFile) => void,
	sendMessage: (key: HubKey, message: unknown) => void,
	objectType: ObjectType,
	objectId: number
) => {
	const knownFilesRef = useRef<Set<string>>(new Set());
	const fileObserverInitializedRef = useRef<boolean>(false);
	const saveWhiteboardFileRef = useRef(saveWhiteboardFile);

	// Update the ref when saveWhiteboardFile changes
	useEffect(() => {
		saveWhiteboardFileRef.current = saveWhiteboardFile;
	}, [saveWhiteboardFile]);

	// Add files helper function
	const addFiles = useCallback(
		async (files: WhiteboardFile[]) => {
			if (!excalidrawRef.current) {
				console.log('Excalidraw ref not ready, cannot add files yet');
				return;
			}

			const filesToAdd: Record<string, WhiteboardFile> = {};
			const knownFiles = knownFilesRef.current;

			for (const file of files) {
				if (!knownFiles.has(file.id)) {
					console.log('New file detected', file);
					knownFiles.add(file.id);

					try {
						const dataURL = await fetchFileAndConvertToBase64DataUrl(file.dataURL);
						if (!dataURL) throw new Error('Failed to convert file to base64: ' + file.dataURL);

						// Create a proper copy of the file object to avoid readonly property issues
						filesToAdd[file.id] = {
							id: file.id,
							mimeType: file.mimeType,
							dataURL: dataURL,
							created: file.created,
							lastRetrieved: Date.now(),
						};
					} catch (error) {
						console.error('Error fetching file data:', error);
						// Remove from known files if we couldn't load it
						knownFiles.delete(file.id);
					}
				}
			}

			if (Object.keys(filesToAdd).length > 0) {
				console.log('Adding the new files to Excalidraw:', Object.keys(filesToAdd));
				excalidrawRef.current.addFiles(filesToAdd);
			}
		},
		[excalidrawRef]
	);

	// File observer setup
	useEffect(() => {
		// Only run when we have whiteboardId and haven't initialized yet
		if (!whiteboardId || fileObserverInitializedRef.current) return;

		// Wait for Excalidraw to be ready
		const initializeFileObserver = () => {
			const api = excalidrawRef.current;
			if (!api) {
				// If API isn't ready yet, try again in next tick
				setTimeout(initializeFileObserver, 10);
				return;
			}

			console.log('Setting up observer for local file changes in Excalidraw');
			fileObserverInitializedRef.current = true;

			const observer = () => {
				const files = api.getFiles() as Record<string, WhiteboardFile>;
				for (const [fileId, file] of Object.entries(files)) {
					if (!knownFilesRef.current.has(fileId)) {
						knownFilesRef.current.add(fileId);
						console.log('New local file in Excalidraw detected', { fileId, file });

						// Create a safe copy of the file object before passing it to the backend
						const safeFile = {
							id: file.id,
							mimeType: file.mimeType,
							dataURL: file.dataURL,
							created: file.created || Date.now(),
						};

						// Use the ref to avoid dependency issues
						saveWhiteboardFileRef.current(whiteboardId, safeFile as WhiteboardFile);
					}
				}
			};

			const removeApiOnChange = api.onChange(observer);

			// Store cleanup function for when component unmounts or whiteboardId changes
			return () => {
				fileObserverInitializedRef.current = false;
				removeApiOnChange();
			};
		};

		const cleanup = initializeFileObserver();
		return cleanup;
	}, [whiteboardId, excalidrawRef]);

	// Handle file save success notification
	const notifyFileSaved = useCallback(
		(saveFileStatus: string, whiteboardId: number | undefined) => {
			if (saveFileStatus === 'SUCCESS' && whiteboardId) {
				console.log('File saved successfully, notifying other clients via SignalR');
				sendMessage('WHITEBOARD', {
					EventType: WhiteboardSignalREventType.FilesUpdated,
					ObjectType: objectType,
					ObjectId: objectId,
					WhiteboardId: whiteboardId,
				} satisfies WhiteboardSignalREvent);
			}
		},
		[sendMessage, objectType, objectId]
	);

	return useMemo(
		() => ({
			addFiles,
			notifyFileSaved,
		}),
		[addFiles, notifyFileSaved]
	);
};
